import { type JSX, useState } from 'react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ValueNode {
  id: string
  label: string
  level: number
  parentValueId: string | null
  children: ValueNode[]
}

export interface CustomFieldValuesTreeProps {
  values: ValueNode[]
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onAddChild: (parentId: string | null, level: number) => void
}

// ---------------------------------------------------------------------------
// assembleTree — pure helper, exported for external use and direct testing
// ---------------------------------------------------------------------------

/**
 * Assembles a flat list of value records into a nested `ValueNode` tree.
 * Orphaned nodes — those whose `parentValueId` is non-null but does not match
 * any node's `id` — are silently excluded from the output.
 */
export function assembleTree(
  flat: Array<{ id: string; parentValueId: string | null; level: number; label: string }>,
): ValueNode[] {
  const byParent: Map<string | null, ValueNode[]> = new Map()
  for (const v of flat) {
    const node: ValueNode = { ...v, children: [] }
    if (!byParent.has(v.parentValueId)) byParent.set(v.parentValueId, [])
    byParent.get(v.parentValueId)!.push(node)
  }
  function attach(node: ValueNode): ValueNode {
    node.children = (byParent.get(node.id) ?? []).map(attach)
    return node
  }
  return (byParent.get(null) ?? []).map(attach)
}

// ---------------------------------------------------------------------------
// Inner recursive node renderer
// ---------------------------------------------------------------------------

const MAX_DEPTH = 4

interface TreeNodeProps {
  node: ValueNode
  expandedIds: Set<string>
  onToggle: (id: string) => void
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onAddChild: (parentId: string | null, level: number) => void
}

function TreeNode({
  node,
  expandedIds,
  onToggle,
  onEdit,
  onDelete,
  onAddChild,
}: TreeNodeProps): JSX.Element {
  const hasChildren = node.children.length > 0
  const isExpanded = expandedIds.has(node.id)
  const canAddChild = node.level < MAX_DEPTH

  // Indentation: 0 for level 1, increasing for deeper levels
  const indentPx = (node.level - 1) * 20

  return (
    <li
      role="treeitem"
      aria-level={node.level}
      aria-expanded={hasChildren ? isExpanded : undefined}
      className="list-none"
    >
      <div
        className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-gray-50"
        style={{ paddingLeft: `${indentPx + 4}px` }}
      >
        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            type="button"
            aria-label={isExpanded ? `Collapse ${node.label}` : `Expand ${node.label}`}
            className="flex h-4 w-4 items-center justify-center rounded text-xs text-gray-400 hover:text-gray-700"
            onClick={() => onToggle(node.id)}
          >
            {isExpanded ? '▾' : '▸'}
          </button>
        ) : (
          // Spacer to keep labels aligned
          <span className="inline-block h-4 w-4" aria-hidden="true" />
        )}

        {/* Node label */}
        <span className="flex-1 text-sm text-gray-800">{node.label}</span>

        {/* Action buttons */}
        <div className="ml-auto flex items-center gap-1">
          {canAddChild && (
            <button
              type="button"
              aria-label={`Add child to ${node.label}`}
              className="rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700"
              onClick={() => onAddChild(node.id, node.level + 1)}
            >
              + Child
            </button>
          )}
          <button
            type="button"
            aria-label={`Edit ${node.label}`}
            className="rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700"
            onClick={() => onEdit(node.id)}
          >
            Edit
          </button>
          <button
            type="button"
            aria-label={`Delete ${node.label}`}
            className="rounded border border-gray-200 px-1.5 py-0.5 text-xs text-red-400 hover:border-red-300 hover:text-red-600"
            onClick={() => onDelete(node.id)}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Children — only rendered when expanded */}
      {hasChildren && isExpanded && (
        <ul role="group" className="list-none p-0">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// CustomFieldValuesTree — main exported component
// ---------------------------------------------------------------------------

/**
 * Recursive tree renderer for custom field values. Displays an indented tree
 * of `ValueNode`s with per-node Edit, Delete, and Add Child actions.
 *
 * - Nodes at level < 4 show an "Add Child" affordance; level-4 nodes do not.
 * - Nodes with children have an expand/collapse toggle; default = expanded.
 * - A top-level "+ Add value" button calls `onAddChild(null, 1)`.
 * - Accessibility: `role="tree"` on the container; `role="treeitem"` +
 *   `aria-level` + `aria-expanded` on each node (spec §9).
 */
export default function CustomFieldValuesTree({
  values,
  onEdit,
  onDelete,
  onAddChild,
}: CustomFieldValuesTreeProps): JSX.Element {
  // Collect all node ids so we can default to expanded.
  function collectIds(nodes: ValueNode[]): string[] {
    return nodes.flatMap((n) => [n.id, ...collectIds(n.children)])
  }

  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(collectIds(values)),
  )

  function handleToggle(id: string): void {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <div className="cfv-tree flex flex-col gap-1">
      <ul role="tree" className="list-none p-0">
        {values.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            expandedIds={expandedIds}
            onToggle={handleToggle}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddChild={onAddChild}
          />
        ))}
      </ul>

      {/* Top-level add affordance */}
      <button
        type="button"
        aria-label="Add root value"
        className="mt-1 self-start rounded border border-dashed border-gray-300 px-2 py-1 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700"
        onClick={() => onAddChild(null, 1)}
      >
        + Add value
      </button>
    </div>
  )
}
